import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Home, Users, Target, User, CreditCard, LogOut, Menu, X, Chrome, ExternalLink, Coffee, Lightbulb, Share2 } from 'lucide-react';
import { ROUTES } from '@traction/shared';
import { useAuth } from '@/contexts/AuthContext';
import { classNames } from '@traction/shared';
import { PageTransition } from '@/components/ui/PageTransition';

const navigation = [
  { name: 'Dashboard', href: ROUTES.DASHBOARD, icon: Home },
  { name: 'Morning Brief', href: ROUTES.MORNING_BRIEF, icon: Coffee },
  { name: 'Lead Criteria', href: ROUTES.SETTINGS, icon: Target },
  { name: 'Leads', href: ROUTES.LEADS, icon: Users },
  { name: 'Needs Match', href: ROUTES.MATCHING, icon: Lightbulb },
  { name: 'Warm Intros', href: ROUTES.WARM_INTROS, icon: Share2 },
  { name: 'Profile', href: '/dashboard/profile', icon: User },
  { name: 'Subscription', href: '/dashboard/subscription', icon: CreditCard },
];

// External link that opens in new window
const extensionLink = {
  name: 'Get Extension',
  href: 'https://www.tractionbee.com/extension',
  icon: Chrome,
};

export function DashboardLayout() {
  const location = useLocation();
  const { logout, profile } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  // Scroll the dashboard panel to top on navigation (window scroll alone misses overflow-auto main)
  useLayoutEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-cacao-dark">
      {/* Mobile header */}
      <div className="md:hidden flex items-center justify-between h-14 border-b border-cacao-dark-tertiary bg-cacao-dark-secondary px-4">
        <Link to={ROUTES.DASHBOARD} className="flex items-center gap-2">
          <span className="text-2xl">🐝</span>
          <span className="font-heading font-semibold text-[#F5F5F5]">Traction Bee</span>
        </Link>
        <button
          type="button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-cacao-muted hover:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-cacao-gold/50"
          aria-expanded={mobileMenuOpen}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-cacao-dark-secondary pt-14">
          <nav className="flex flex-col gap-1 p-4">
            {navigation.map((item) => {
              const isActive = item.href === ROUTES.DASHBOARD
                ? location.pathname === item.href
                : location.pathname.startsWith(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={classNames(
                    'flex items-center gap-3 rounded-md px-3 py-3 text-base font-medium transition-colors',
                    isActive
                      ? 'bg-cacao-gold text-cacao-dark'
                      : 'text-cacao-muted hover:bg-cacao-dark-tertiary hover:text-white'
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
            {/* Extension link - opens in new window */}
            <a
              href={extensionLink.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 rounded-md px-3 py-3 text-base font-medium text-cacao-muted hover:bg-cacao-dark-tertiary hover:text-white transition-colors"
            >
              <extensionLink.icon className="h-5 w-5" />
              {extensionLink.name}
              <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
            </a>
            <button
              onClick={() => { setMobileMenuOpen(false); logout(); }}
              className="flex items-center gap-3 rounded-md px-3 py-3 text-base font-medium text-cacao-muted hover:bg-cacao-dark-tertiary hover:text-white transition-colors mt-4 border-t border-cacao-dark-tertiary pt-4"
            >
              <LogOut className="h-5 w-5" />
              Log out
            </button>
          </nav>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden md:block w-64 border-r border-cacao-dark-tertiary bg-cacao-dark-secondary relative">
        <div className="flex h-14 items-center border-b border-cacao-dark-tertiary px-6">
          <Link to={ROUTES.DASHBOARD} className="flex items-center gap-2">
            <span className="text-2xl">🐝</span>
            <span className="font-heading font-semibold text-[#F5F5F5]">Traction Bee</span>
          </Link>
        </div>

        <nav className="flex flex-col gap-1 p-4">
          {navigation.map((item) => {
            const isActive = item.href === ROUTES.DASHBOARD
              ? location.pathname === item.href
              : location.pathname.startsWith(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={classNames(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-cacao-gold text-cacao-dark'
                    : 'text-cacao-muted hover:bg-cacao-dark-tertiary hover:text-white'
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.name}
              </Link>
            );
          })}
          {/* Extension link - opens in new window */}
          <a
            href={extensionLink.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-cacao-muted hover:bg-cacao-dark-tertiary hover:text-white transition-colors"
          >
            <extensionLink.icon className="h-4 w-4" />
            {extensionLink.name}
            <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
          </a>
        </nav>

        <div className="absolute bottom-0 left-0 w-64 border-t border-cacao-dark-tertiary p-4">
          <div className="mb-3 text-sm">
            <p className="font-medium text-[#F5F5F5]">{profile?.first_name || profile?.full_name || 'User'}</p>
          </div>
          <button
            onClick={() => logout()}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-cacao-muted hover:bg-cacao-dark-tertiary hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </aside>

      <main ref={mainRef} className="flex-1 overflow-auto bg-cacao-dark">
        <PageTransition key={location.pathname} className="min-h-full">
          <Outlet />
        </PageTransition>
      </main>
    </div>
  );
}
